import { useEffect, useRef } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { districtsFor } from "@/lib/districts";
import { factsFromProfile } from "@/lib/onboarding-facts";
import { wizardSteps } from "@/lib/onboarding-steps";
import { sanitizeIntents } from "@/lib/platform-intents";
import { onboardingStateKey } from "@/lib/onboarding-state";

/**
 * Существующие пользователи (мягкий мастер): один раз на /home.
 * Есть незаполненные обязательные шаги — отправляем в мастер и ставим onboarding_prompted_at;
 * нет — молча ставим onboarding_completed_at. Больше не перенаправляем.
 */
export function ExistingUserOnboardingNudge() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const checked = useRef(false);

  useEffect(() => {
    if (pathname !== "/home" || !user?.id || checked.current) return;
    checked.current = true;
    const uid = user.id;
    (async () => {
      try {
        const { data: p, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", uid)
          .maybeSingle();
        if (error || !p) return;
        if (p.onboarding_gate || p.onboarding_completed_at || p.onboarding_prompted_at) return;
        const { data: ui } = await supabase
          .from("user_platform_intents")
          .select("intents")
          .eq("user_id", uid)
          .maybeSingle();
        const facts = {
          ...factsFromProfile(p, districtsFor(p.city).length > 0),
          intents: sanitizeIntents(ui?.intents),
        };
        const now = new Date().toISOString();
        // Новый короткий мастер: показываем, если не хватает намерений, города или интересов.
        if (wizardSteps(facts).length > 0) {
          const { error: e } = await supabase
            .from("profiles")
            .update({ onboarding_prompted_at: now })
            .eq("user_id", uid);
          if (!e) navigate({ to: "/profile/setup" });
        } else {
          await supabase
            .from("profiles")
            .update({ onboarding_completed_at: now })
            .eq("user_id", uid);
          qc.invalidateQueries({ queryKey: onboardingStateKey(uid) });
        }
      } catch {
        /* тихо: это лишь подсказка */
      }
    })();
  }, [pathname, user?.id, navigate, qc]);

  return null;
}
